import { Autocomplete, Box, Stack, TextField, Typography } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';

interface CountryCode {
  code: string;
  label: string;
  dialCode: string;
}

const countryCodes: CountryCode[] = [
  { code: 'US', label: 'United States', dialCode: '+1' },
  { code: 'CA', label: 'Canada', dialCode: '+1' },
  { code: 'GB', label: 'United Kingdom', dialCode: '+44' },
  { code: 'AU', label: 'Australia', dialCode: '+61' },
  { code: 'DE', label: 'Germany', dialCode: '+49' },
  { code: 'FR', label: 'France', dialCode: '+33' },
  { code: 'JP', label: 'Japan', dialCode: '+81' },
  { code: 'KR', label: 'South Korea', dialCode: '+82' },
  { code: 'CN', label: 'China', dialCode: '+86' },
  { code: 'IN', label: 'India', dialCode: '+91' },
  { code: 'MX', label: 'Mexico', dialCode: '+52' },
  { code: 'BR', label: 'Brazil', dialCode: '+55' },
  { code: 'ES', label: 'Spain', dialCode: '+34' },
  { code: 'IT', label: 'Italy', dialCode: '+39' },
  { code: 'NL', label: 'Netherlands', dialCode: '+31' },
  { code: 'SE', label: 'Sweden', dialCode: '+46' },
  { code: 'NO', label: 'Norway', dialCode: '+47' },
  { code: 'DK', label: 'Denmark', dialCode: '+45' },
  { code: 'FI', label: 'Finland', dialCode: '+358' },
  { code: 'CH', label: 'Switzerland', dialCode: '+41' },
];

interface PhoneNumberInputProps {
  label: string;
  value?: string; // Full phone number like "+11234567890"
  onChange: (fullPhoneNumber: string) => void;
  required?: boolean;
  error?: boolean;
  helperText?: string;
}

// Parse a full phone number into country code and local number
const parsePhoneNumber = (
  fullNumber?: string,
): { countryCode: CountryCode | null; localNumber: string } => {
  if (!fullNumber || !fullNumber.startsWith('+')) {
    return { countryCode: countryCodes[0], localNumber: '' }; // Default to US
  }

  // Find matching country code
  const countryCode = countryCodes.find((country) => fullNumber.startsWith(country.dialCode));

  if (countryCode) {
    const localNumber = fullNumber.slice(countryCode.dialCode.length);
    return { countryCode, localNumber };
  }

  // Fallback to US if no match found
  return { countryCode: countryCodes[0], localNumber: fullNumber.slice(1) };
};

// Format local number for display with cursor position tracking
const formatLocalNumber = (
  value: string,
  dialCode: string,
  cursorPos: number,
): { formatted: string; newCursorPos: number } => {
  // Remove all non-numeric characters
  const numbersOnly = value.replace(/\D/g, '');

  if (dialCode === '+1') {
    // US/Canada formatting: (123) 456-7890
    // Limit to exactly 10 digits for US numbers
    const limitedNumbers = numbersOnly.slice(0, 10);
    let formatted = '';
    if (limitedNumbers.length === 0) {
      return { formatted: '', newCursorPos: 0 };
    }
    if (limitedNumbers.length <= 3) {
      formatted = `(${limitedNumbers}`;
    } else if (limitedNumbers.length <= 6) {
      formatted = `(${limitedNumbers.slice(0, 3)}) ${limitedNumbers.slice(3)}`;
    } else {
      formatted = `(${limitedNumbers.slice(0, 3)}) ${limitedNumbers.slice(
        3,
        6,
      )}-${limitedNumbers.slice(6)}`;
    }

    // Calculate cursor position for US format
    let newCursorPos = cursorPos;
    const originalDigitPos = Math.max(
      0,
      Math.min(10, cursorPos - value.slice(0, cursorPos).replace(/\d/g, '').length),
    );

    if (originalDigitPos === 0) newCursorPos = 1; // After opening parenthesis
    else if (originalDigitPos <= 3) newCursorPos = 1 + originalDigitPos;
    else if (originalDigitPos <= 6) newCursorPos = 6 + (originalDigitPos - 3);
    else newCursorPos = 10 + (originalDigitPos - 6);

    return { formatted, newCursorPos: Math.min(newCursorPos, formatted.length) };
  } else {
    // International formatting: just add spaces every 3-4 digits
    // Limit based on E.164 standard (15 digits total minus country code length)
    const countryCodeDigits = dialCode.slice(1).length; // Remove the '+'
    const maxLocalDigits = 15 - countryCodeDigits;
    const limitedNumbers = numbersOnly.slice(0, maxLocalDigits);
    let formatted = '';
    if (limitedNumbers.length <= 4) {
      formatted = limitedNumbers;
    } else if (limitedNumbers.length <= 8) {
      formatted = `${limitedNumbers.slice(0, 4)} ${limitedNumbers.slice(4)}`;
    } else {
      formatted = `${limitedNumbers.slice(0, 4)} ${limitedNumbers.slice(
        4,
        8,
      )} ${limitedNumbers.slice(8)}`;
    }

    // Simple cursor positioning for international numbers
    const originalDigitPos = Math.max(
      0,
      Math.min(maxLocalDigits, cursorPos - value.slice(0, cursorPos).replace(/\d/g, '').length),
    );
    let newCursorPos = originalDigitPos;
    if (originalDigitPos > 4) newCursorPos += 1; // Account for first space
    if (originalDigitPos > 8) newCursorPos += 1; // Account for second space

    return { formatted, newCursorPos: Math.min(newCursorPos, formatted.length) };
  }
};

export const PhoneNumberInput: React.FC<PhoneNumberInputProps> = ({
  label,
  value,
  onChange,
  required = false,
  error = false,
  helperText,
}) => {
  const { countryCode: initialCountryCode, localNumber: initialLocalNumber } =
    parsePhoneNumber(value);

  const [selectedCountryCode, setSelectedCountryCode] = useState<CountryCode | null>(
    initialCountryCode,
  );
  const [localNumber, setLocalNumber] = useState(initialLocalNumber);
  const phoneInputRef = useRef<HTMLInputElement>(null);

  // Update internal state when external value changes (e.g., when editing existing record)
  useEffect(() => {
    const { countryCode: newCountryCode, localNumber: newLocalNumber } = parsePhoneNumber(value);
    setSelectedCountryCode(newCountryCode);
    if (newLocalNumber && newCountryCode) {
      // Format the initial local number for display
      const { formatted } = formatLocalNumber(newLocalNumber, newCountryCode.dialCode, 0);
      setLocalNumber(formatted);
    } else {
      setLocalNumber(newLocalNumber);
    }
  }, [value]);

  const handleCountryCodeChange = (newCountryCode: CountryCode | null) => {
    setSelectedCountryCode(newCountryCode);
    if (newCountryCode && localNumber) {
      const numbersOnly = localNumber.replace(/\D/g, '');
      // Reformat the local number with the new country code
      const { formatted } = formatLocalNumber(numbersOnly, newCountryCode.dialCode, 0);
      setLocalNumber(formatted);
      const fullNumber = `${newCountryCode.dialCode}${numbersOnly}`;
      onChange(fullNumber);
    } else if (!newCountryCode) {
      onChange('');
    }
  };

  const handleLocalNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newLocalNumber = e.target.value;
    const cursorPos = e.target.selectionStart || 0;

    // Extract only digits for storage
    let numbersOnly = newLocalNumber.replace(/\D/g, '');

    // Apply length limits based on E.164 standard (max 15 digits total)
    if (selectedCountryCode?.dialCode === '+1') {
      // Limit US/Canada numbers to 10 digits local (11 total with +1)
      numbersOnly = numbersOnly.slice(0, 10);
    } else if (selectedCountryCode) {
      // Limit international numbers: 15 total digits minus country code length
      const countryCodeDigits = selectedCountryCode.dialCode.slice(1).length; // Remove the '+'
      const maxLocalDigits = 15 - countryCodeDigits;
      numbersOnly = numbersOnly.slice(0, maxLocalDigits);
    }

    // Format for display with cursor positioning
    const { formatted, newCursorPos } = selectedCountryCode
      ? formatLocalNumber(newLocalNumber, selectedCountryCode.dialCode, cursorPos)
      : { formatted: numbersOnly, newCursorPos: cursorPos };

    setLocalNumber(formatted);

    // Set cursor position after React updates
    setTimeout(() => {
      if (phoneInputRef.current) {
        phoneInputRef.current.setSelectionRange(newCursorPos, newCursorPos);
      }
    }, 0);

    // Send full phone number to parent
    if (selectedCountryCode && numbersOnly) {
      const fullNumber = `${selectedCountryCode.dialCode}${numbersOnly}`;
      onChange(fullNumber);
    } else {
      onChange('');
    }
  };

  return (
    <Box>
      <Typography fontSize="16px" fontWeight={400} mb={1}>
        {label} {required && '*'}
      </Typography>
      <Stack direction="row" spacing={1}>
        <Autocomplete
          value={selectedCountryCode || undefined}
          onChange={(_, newValue) => handleCountryCodeChange(newValue)}
          options={countryCodes}
          getOptionLabel={(option) => `${option.dialCode} ${option.label}`}
          disableClearable
          renderOption={(props, option) => (
            <Box component="li" {...props} key={option.code}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography fontWeight="bold">{option.dialCode}</Typography>
                <Typography>{option.label}</Typography>
              </Stack>
            </Box>
          )}
          renderInput={(params) => (
            <TextField {...params} placeholder="Country" variant="outlined" />
          )}
          sx={{ width: 180 }}
        />
        <TextField
          inputRef={phoneInputRef}
          value={localNumber}
          onChange={handleLocalNumberChange}
          placeholder={selectedCountryCode?.dialCode === '+1' ? '(123) 456-7890' : 'Phone number'}
          variant="outlined"
          fullWidth
          error={error}
        />
      </Stack>
      {helperText && (
        <Typography
          fontSize="14px"
          fontWeight={400}
          variant="body2"
          sx={{ color: error ? '#e53935' : 'text.secondary', mt: 0.5 }}
        >
          {helperText}
        </Typography>
      )}
    </Box>
  );
};

export default PhoneNumberInput;
